import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTimezoneListener } from "~/components/Hooks/useTimezoneListener";
import { LoaderWrapper } from "~/components/LoaderWrapper";
import OnboardingLayout from "~/pages/Onboarding/OnboardingLayout";
import { arrayOfAvailableReactions } from "~/schemas/schema.available_reactions";
import { arrayOfCurrencies } from "~/schemas/schema.currencies";
import { arrayOfDaysOff } from "~/schemas/schema.days_off";
import { arrayOfExpenseCategories } from "~/schemas/schema.expense_categories";
import { arrayOfJobTypes } from "~/schemas/schema.job_types";
import { arrayOfLabels } from "~/schemas/schema.labels";
import { arrayOfLanguages } from "~/schemas/schema.languages";
import { arrayOfProjectCategories } from "~/schemas/schema.projectCategories";
import { arrayOfProjects } from "~/schemas/schema.projects";
import { arrayOfShortcuts } from "~/schemas/schema.shortcuts";
import { arrayOfUsers } from "~/schemas/schema.users";
import AppService from "~/services/AppService";
import ConfigOptionsService from "~/services/ConfigOptionsService.js";
import DayOffsService from "~/services/DayOffsService.js";
import localizationService from "~/services/LocalizationService";
import ShortcutsService from "~/services/ShortcutsService";
import TeamsService from "~/services/TeamsService.js";
import { FETCH_AVAILABLE_REACTIONS_SUCCESS } from "~/store/action_types";
import { FETCH_LANGUAGES_SUCCESS, FETCH_USERS_SUCCESS } from "~/store/action_types";
import {
  LOAD_TEAMS,
  FETCH_PROJECTS_SUCCESS,
  FETCH_PERSONALIZED_CONFIG_OPTIONS_SUCCESS,
  FETCH_SHORTCUTS_SUCCESS,
  FETCH_LABELS_SUCCESS,
  ANGULAR_INIT_PROJECT_CATEGORIES,
  FETCH_JOB_TYPES_SUCCESS,
  ANGULAR_INIT_CURRENCIES,
  ANGULAR_INIT_EXPENSE_CATEGORIES,
  FETCH_DAYS_OFF_SUCCESS,
  FETCH_CONFIG_OPTIONS_SUCCESS,
} from "~/store/action_types/index.jsx";
import { ANGULAR_INIT } from "~/store/action_types/index.jsx";
import { getExternalLoginUrl } from "~/store/selectors/selector.configOptions";
import { getCsrfValidatorName } from "~/store/selectors/selector.configOptions.jsx";
import Angie from "~/utils/angie";
import { UnauthorizedError } from "~/utils/errors";

export const AppResolver = ({ children }) => {
  const dispatch = useDispatch();
  const externalLoginUrl = useSelector(getExternalLoginUrl);
  const csrfValidatorName = useSelector(getCsrfValidatorName);

  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState(null);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const listen = useTimezoneListener();

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (Angie.page_reload_needed) {
        Angie.app_reloading = true;
        window.location.reload(true);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [pathname]);

  useEffect(() => {
    if (loading) {
      Promise.all([
        AppService.getInitial(),
        AppService.getUserSession(),
        TeamsService.getAll(),
        ShortcutsService.getAll(),
        ConfigOptionsService.getAllByQuery("time_workdays"),
        DayOffsService.getAll(),
      ])
        .then(([initial, session, teams, shortcuts, configOptions, daysOff]) => {
          const {
            settings: config_options,
            available_reactions,
            project_categories,
            project_labels,
            task_labels,
            currencies,
            expense_categories,
            languages,
          } = initial;
          const {
            companies,
            instance_id,
            settings: personalized_config_options,
            favorites,
            users,
            logged_user_id,
            system_notifications,
            homepage,
            projects,
            job_types,
            feed_token,
            new_features_count,
          } = session;

          const logged_user = users.find((user) => user.id === logged_user_id);

          localizationService.configure(
            {
              language_id: logged_user.language_id,
              dow: personalized_config_options.time_first_week_day,
              timezone: personalized_config_options.timezone,
              timezone_autodetect: personalized_config_options.timezone_autodetect,
              format_time: personalized_config_options.format_time,
            },
            languages,
            true
          );

          Angie.user_session_data.new_features_count = new_features_count;

          Promise.all([
            dispatch({
              type: FETCH_AVAILABLE_REACTIONS_SUCCESS,
              response: {
                available_reactions,
              },
              schema: {
                available_reactions: arrayOfAvailableReactions,
              },
            }),
            dispatch({
              type: FETCH_USERS_SUCCESS,
              response: users,
              schema: arrayOfUsers,
            }),
            dispatch({
              type: ANGULAR_INIT,
              response: {
                logged_user,
                config_options,
                personalized_config_options,
                companies,
                instance_id,
                favorites,
                system_notifications,
                homepage,
                feed_token,
              },
            }),
            dispatch({
              type: LOAD_TEAMS,
              response: teams,
            }),
            dispatch({
              type: FETCH_PROJECTS_SUCCESS,
              response: projects,
              schema: arrayOfProjects,
            }),
            dispatch({
              type: FETCH_SHORTCUTS_SUCCESS,
              response: shortcuts,
              schema: arrayOfShortcuts,
            }),
            dispatch({
              type: FETCH_LABELS_SUCCESS,
              response: [...project_labels, ...task_labels],
              schema: arrayOfLabels,
            }),
            dispatch({
              type: ANGULAR_INIT_PROJECT_CATEGORIES,
              response: project_categories,
              schema: arrayOfProjectCategories,
            }),
            dispatch({
              type: FETCH_JOB_TYPES_SUCCESS,
              response: job_types,
              schema: arrayOfJobTypes,
            }),
            dispatch({
              type: ANGULAR_INIT_CURRENCIES,
              response: currencies,
              schema: arrayOfCurrencies,
            }),
            dispatch({
              type: ANGULAR_INIT_EXPENSE_CATEGORIES,
              response: expense_categories,
              schema: arrayOfExpenseCategories,
            }),
            dispatch({
              type: FETCH_CONFIG_OPTIONS_SUCCESS,
              response: configOptions,
            }),
            dispatch({
              type: FETCH_DAYS_OFF_SUCCESS,
              response: daysOff,
              schema: arrayOfDaysOff,
            }),
            dispatch({
              type: FETCH_LANGUAGES_SUCCESS,
              response: languages,
              schema: arrayOfLanguages,
            }),
          ]).then(() => {
            setLoading(false);
            listen();
            if (
              initial?.settings?.on_demand &&
              (initial?.settings?.show_set_password || initial?.settings?.show_sample_projects_wizard_step)
            ) {
              navigate("/onboarding");
            }
          });
        })
        .catch((error) => {
          dispatch({
            type: FETCH_PERSONALIZED_CONFIG_OPTIONS_SUCCESS,
            response: error.data?.settings,
          });
          setLoginError(error);
          setLoading(false);
        });
    }
  }, [dispatch, loading, listen, navigate]);

  if (loading) {
    if (pathname === "/onboarding") {
      return <OnboardingLayout />;
    }
    return <LoaderWrapper />;
  }

  // init
  window.csrfValidatorName = csrfValidatorName;

  if (loginError instanceof UnauthorizedError && externalLoginUrl) {
    window.location.href = externalLoginUrl;
    return null;
  }

  return children;
};

AppResolver.propTypes = {
  children: PropTypes.any,
};
